import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Clock,
  Search,
  ArrowUp
} from 'lucide-react';
import { allSystemDesignTopics } from '../../data/systemDesignData';

const SystemDesignAllTopics: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Get all topics from all categories
  const allTopics = allSystemDesignTopics.flatMap(category =>
    category.topics.map(topic => ({ ...topic, category: category.category }))
  );

  // Filter topics
  const filteredTopics = allTopics.filter(topic => {
    const matchesSearch = topic.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      topic.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDifficulty = selectedDifficulty === 'all' || topic.difficulty === selectedDifficulty;
    const matchesCategory = selectedCategory === 'all' || topic.category === selectedCategory;

    return matchesSearch && matchesDifficulty && matchesCategory;
  });

  // Group filtered topics by category
  const groupedTopics = allSystemDesignTopics.map(category => ({
    ...category,
    topics: category.topics.filter(topic => {
      const topicWithCategory = { ...topic, category: category.category };
      return filteredTopics.includes(topicWithCategory);
    })
  })).filter(category => category.topics.length > 0);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'badge-green';
      case 'intermediate':
        return 'badge-yellow';
      case 'advanced':
        return 'badge-orange';
      case 'expert':
        return 'badge-red';
      default:
        return 'badge-gray';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pt-16">
      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all duration-300 z-40"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}

      <div className="max-w-full mx-auto">
        <div className="grid grid-cols-12 gap-0">
          {/* Left Sidebar - Filters */}
          <div className="col-span-3 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 min-h-screen">
            <div className="sticky top-24 p-6">
              {/* Breadcrumb */}
              <div className="mb-6">
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <Link to="/system-design" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    System Design
                  </Link>
                  <ChevronRight className="w-4 h-4" />
                  <span className="text-gray-900 dark:text-gray-100">All Topics</span>
                </div>
              </div>

              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 uppercase tracking-wide">
                Filters
              </h3>

              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search Topics
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search topics..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Categories</option>
                  {allSystemDesignTopics.map((category) => (
                    <option key={category.category} value={category.category}>
                      {category.category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Difficulty Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Difficulty
                </label>
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Levels</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="expert">Expert</option>
                </select>
              </div>

              {/* Quick Stats */}
              <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Quick Stats</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Total Topics:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{allTopics.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Filtered:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{filteredTopics.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Categories:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{allSystemDesignTopics.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="col-span-6 bg-white dark:bg-gray-900">
            <div className="px-8 py-8">
              {/* Header */}
              <div className="mb-8">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4 leading-tight">
                  All System Design Topics
                </h1>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  Master system design concepts from fundamentals to advanced architectural patterns used by major tech companies.
                </p>
              </div>

              {/* Results Summary */}
              <div className="mb-6">
                <p className="text-gray-600 dark:text-gray-400">
                  Showing {filteredTopics.length} of {allTopics.length} topics
                </p>
              </div>

              {/* Topics by Category */}
              <div className="space-y-12">
                {groupedTopics.map((category) => (
                  <div key={category.category}>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                      {category.category}
                    </h2>

                    <div className="grid grid-cols-1 gap-6">
                      {category.topics.map((topic) => (
                        <Link
                          key={topic.id}
                          to={`/system-design/${topic.id}`}
                          className="group block bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-300"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-3">
                                <span className="text-2xl">{topic.icon}</span>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                  {topic.title}
                                </h3>
                              </div>

                              <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                                {topic.description}
                              </p>

                              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                                <div className="flex items-center space-x-1">
                                  <Clock className="w-4 h-4" />
                                  <span>{topic.estimatedTime}</span>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(topic.difficulty)}`}>
                                  {topic.difficulty.charAt(0).toUpperCase() + topic.difficulty.slice(1)}
                                </span>
                              </div>
                            </div>

                            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all flex-shrink-0 ml-4" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* No Results */}
              {filteredTopics.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <Search className="w-16 h-16 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No topics found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Try adjusting your search criteria or filters.
                  </p>
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedDifficulty('all');
                      setSelectedCategory('all');
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Additional Content */}
          <div className="col-span-3 bg-gray-50 dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 min-h-screen">
            <div className="sticky top-24 p-6">
              {/* Learning Path */}
              <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mb-6">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">🎯 Recommended Learning Path</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center text-green-600 dark:text-green-400 text-xs font-bold">1</div>
                    <Link to="/system-design/scalability" className="text-blue-600 dark:text-blue-400 hover:underline">Scalability Fundamentals</Link>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center text-yellow-600 dark:text-yellow-400 text-xs font-bold">2</div>
                    <Link to="/system-design/load-balancing" className="text-blue-600 dark:text-blue-400 hover:underline">Load Balancing</Link>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs font-bold">3</div>
                    <Link to="/system-design/databases" className="text-blue-600 dark:text-blue-400 hover:underline">Database Design</Link>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-400 text-xs font-bold">4</div>
                    <Link to="/system-design/caching" className="text-blue-600 dark:text-blue-400 hover:underline">Caching Strategies</Link>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center text-red-600 dark:text-red-400 text-xs font-bold">5</div>
                    <Link to="/system-design/security" className="text-blue-600 dark:text-blue-400 hover:underline">System Security</Link>
                  </div>
                </div>
              </div>

              {/* Popular Topics */}
              <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mb-6">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">🔥 Popular Topics</h4>
                <div className="space-y-3">
                  <Link to="/system-design/databases" className="block text-sm text-blue-600 dark:text-blue-400 hover:underline">
                    Database Design
                  </Link>
                  <Link to="/system-design/scalability" className="block text-sm text-blue-600 dark:text-blue-400 hover:underline">
                    Scalability Fundamentals
                  </Link>
                  <Link to="/system-design/microservices" className="block text-sm text-blue-600 dark:text-blue-400 hover:underline">
                    Microservices Architecture
                  </Link>
                </div>
              </div>

              {/* Progress Tracker */}
              <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mb-6">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">📊 Your Progress</h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-400">Core Concepts</span>
                      <span className="text-gray-900 dark:text-gray-100">3/6</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: '50%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-400">Case Studies</span>
                      <span className="text-gray-900 dark:text-gray-100">1/3</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full" style={{ width: '33%' }}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* DISABLED: Newsletter section — uncomment when newsletter backend is ready
              <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">📧 Stay Updated</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  Get weekly system design insights and new content notifications.
                </p>
                <div className="space-y-2">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                  <button className="w-full px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors">
                    Subscribe
                  </button>
                </div>
              </div>
              */}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemDesignAllTopics;
